import { auth } from "./firebase";

// Single API endpoint discovery. Production uses the deployed `api` Cloud
// Function URL (set via VITE_API_BASE_URL); dev defaults to the emulator.
// When emulators are on, the URL is derived from VITE_FIREBASE_PROJECT_ID
// so it matches whichever project the emulator was started with.
const PROJECT_ID = import.meta.env.VITE_FIREBASE_PROJECT_ID;
const API_BASE_URL =
  import.meta.env.VITE_USE_EMULATORS === "true"
    ? `http://localhost:5001/${PROJECT_ID}/europe-west1/api`
    : (import.meta.env.VITE_API_BASE_URL ??
       `https://europe-west1-${PROJECT_ID}.cloudfunctions.net/api`);

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: unknown;

  constructor(code: string, message: string, status: number, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (auth?.currentUser) {
    const token = await auth.currentUser.getIdToken();
    headers.Authorization = `Bearer ${token}`;
  }
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers,
    body: body == null ? undefined : JSON.stringify(body),
    credentials: "omit",
  });
  const text = await res.text();
  const json = text ? (JSON.parse(text) as unknown) : null;
  if (!res.ok) {
    const err = json as { error?: { code?: string; message?: string; details?: unknown } } | null;
    throw new ApiError(
      err?.error?.code ?? "internal",
      err?.error?.message ?? `HTTP ${res.status}`,
      res.status,
      err?.error?.details,
    );
  }
  return json as T;
}

async function requestBinary<T>(
  method: "POST" | "PUT",
  path: string,
  blob: Blob,
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": blob.type || "application/octet-stream",
  };
  if (auth?.currentUser) {
    const token = await auth.currentUser.getIdToken();
    headers.Authorization = `Bearer ${token}`;
  }
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers,
    body: blob,
    credentials: "omit",
  });
  const text = await res.text();
  const json = text ? (JSON.parse(text) as unknown) : null;
  if (!res.ok) {
    const err = json as { error?: { code?: string; message?: string; details?: unknown } } | null;
    throw new ApiError(
      err?.error?.code ?? "internal",
      err?.error?.message ?? `HTTP ${res.status}`,
      res.status,
      err?.error?.details,
    );
  }
  return json as T;
}

// GET that returns the raw response body as a Blob (file downloads). The
// regular `request` blindly JSON.parses every body, which breaks on CSV — use
// this for the observability export and any other binary/text-file endpoint.
async function requestBlob(path: string): Promise<Blob> {
  const headers: Record<string, string> = {};
  if (auth?.currentUser) {
    const token = await auth.currentUser.getIdToken();
    headers.Authorization = `Bearer ${token}`;
  }
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: "GET",
    headers,
    credentials: "omit",
  });
  if (!res.ok) {
    const text = await res.text();
    type ApiErrorBody = {
      error?: { code?: string; message?: string; details?: unknown };
    };
    let err: ApiErrorBody | null = null;
    try {
      err = text ? (JSON.parse(text) as ApiErrorBody) : null;
    } catch {
      // Non-JSON error body — fall through to the generic message.
    }
    throw new ApiError(
      err?.error?.code ?? "internal",
      err?.error?.message ?? `HTTP ${res.status}`,
      res.status,
      err?.error?.details,
    );
  }
  return res.blob();
}

export const api = {
  get: <T = unknown>(path: string) => request<T>("GET", path),
  post: <T = unknown>(path: string, body?: unknown) => request<T>("POST", path, body),
  patch: <T = unknown>(path: string, body?: unknown) => request<T>("PATCH", path, body),
  delete: <T = unknown>(path: string) => request<T>("DELETE", path),
  postBinary: <T = unknown>(path: string, blob: Blob) =>
    requestBinary<T>("POST", path, blob),
  getBlob: (path: string) => requestBlob(path),
};
